import Repository from './Repository';

class ClientRepository extends Repository {
	/**
	 * Resolves an array of client objects from the API.
	 *
	 * @returns {Promise<array>}
	 */
	async all () {
		const include = [
			'groups',
			'translator-connections',
			'translator-connections-count',
			'groups-count',
			'online-parent-devices-count',
			'offline-parent-devices-count',
			'syncing-parent-devices-count',
			'cwSettings'
		].join(',');

		return this.getWithToken(`/clients?include=${include}`);
	}

	/**
	 * Resolves a single client from the given ID.
	 *
	 * @param {int} id The client ID to retrieve
	 * @returns {Promise<object>}
	 */
	async find (id) {
		return this.getWithToken(`/clients/${id}`);
	}

	/**
	 * Creates a new client.
	 *
	 * @param {string} name The client name
	 * @param {int} threshold The threshold devices will use for the "is_online" status.
	 * @param {string} thresholdUnit The unit for the threshold
	 * @param {array} connections An array of Translator Connection IDs
	 * @param {bool} isActive Flag that shows if the client is active.
	 * @returns {Promise<object>} The newly-created Client object.
	 */
	async store (name, threshold, thresholdUnit, connections, isActive) {
		return this.postWithToken('/clients', {
			name,
			threshold,
			threshold_unit: thresholdUnit,
			connections,
			is_active: isActive
		});
	}

	/**
	 * Creates a new client.
	 *
	 * @param {int} clientId The client ID to update
	 * @param {string} name The client name
	 * @param {int} threshold The threshold devices will use for the "is_online" status.
	 * @param {string} thresholdUnit The unit for the threshold
	 * @param {array} connections An array of Translator Connection IDs
	 * @param {bool} isActive Flag that shows if the client is active.
	 * @returns {Promise<object>} The newly updated Client object.
	 */
	async update (clientId, name, threshold, thresholdUnit, connections, isActive) {
		return this.putWithToken(`/clients/${clientId}`, {
			name,
			threshold,
			threshold_unit: thresholdUnit,
			connections,
			is_active: isActive
		});
	}

	/**
	 * Deletes the client with the given ID.
	 *
	 * @param {int} clientId The client ID to delete
	 * @returns {Promise}
	 */
	// async delete (clientId) {
	//	 return this.deleteWithToken(`/clients/${clientId}`)
	// }

	/**
	 * Retrieves an array of monitors from the given client ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @returns {Promise<array>}
	 */
	async getMonitors (clientId) {
		const include = [
			'threshold-setting',
			'monitor-action-group-template.monitor-action-groups.success-action',
			'monitor-action-group-template.monitor-action-groups.failure-action'
			// 'audits',
			// 'audits.monitorAudits',
			// 'audits.monitorAudits.monitored',
			// 'notifications',
			// 'notifications.monitored'
		].join(',');

		return this.getWithToken(`/clients/${clientId}/monitors?include=${include}`);
	}

	/**
	 * Retrieves an array of monitor types from the given client ID.
	 *
	 * @param {int} clientId The client ID to query against
	 */
	async getMonitorTypes (clientId) {
		return this.getWithToken(`/clients/${clientId}/monitors/types`);
	}

	/**
	 * Retrieves the given monitor type from the given client ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {string} monitorType The monitor type key
	 * @param {object} settings The settings to be sent with the query
	 */
	async getMonitorType (clientId, monitorType, settings) {
		const query = Object.keys(settings)
			.filter(key => settings[key] !== '')
			.map(key => key + '=' + settings[key]).join('&');

		return this.getWithToken(`/clients/${clientId}/monitors/types/${monitorType}?${query}`);
	}

	/**
	 * retrieves the notifications for a client
	 *
	 * @param clientId
	 * @returns {Promise<Array|Object|string>}
	 */
	async getNotifications (clientId) {
		return this.getWithToken(`/clients/${clientId}/notifications`);
	}

	deleteClient (clientId) {
		return this.deleteWithToken(`/clients/${clientId}`);
	}

	getRootGroups (clientId) {
		return this.getWithToken(`/clients/${clientId}/root-groups`);
	}

	getMagTemplates (clientId) {
		const include = [
			'monitors-count',
			'monitor-action-groups.success-action',
			'monitor-action-groups.failure-action',
			'monitor-action-groups.success-action.monitorActionSettings',
			'monitor-action-groups.failure-action.monitorActionSettings',
			'success-actions-count',
			'failure-actions-count'
		];

		return this.getWithToken(`/clients/${clientId}/mag-templates?include=${include.join(',')}`);
	}

	roleAll () {
		return this.getWithToken('/clients/role-list');
	}
}

export default new ClientRepository();
